import React from "react";
import { AlertCircle, Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import { ProposalFolderTree } from "./ProposalFolderTree";
import { DIMENSION_LABEL_KEYS, ORGANIZE_DIMENSIONS } from "../../types/organize";
import type { OrganizeDimension, TagProposalPreview } from "../../types/organize";

interface OrganizeTagPickerProps {
  dimensions: OrganizeDimension[];
  onChange: (dimensions: OrganizeDimension[]) => void;
  preview: TagProposalPreview | null;
  isLoading: boolean;
  isError: boolean;
  capExceeded: boolean;
  maxFolders: number;
}

/**
 * Pick which tag dimensions to nest by, and in what order, with the resulting
 * structure drawn underneath as it changes. The preview comes from the same SQL
 * that stores the proposal, so what is shown here is what gets applied.
 */
export function OrganizeTagPicker({
  dimensions,
  onChange,
  preview,
  isLoading,
  isError,
  capExceeded,
  maxFolders,
}: OrganizeTagPickerProps) {
  const { t } = useLanguage();

  const label = (d: OrganizeDimension) =>
    t.documents.tagFilter[DIMENSION_LABEL_KEYS[d] as keyof typeof t.documents.tagFilter] as string;

  // Clicking an inactive chip appends it (so the click order IS the nesting
  // order); clicking an active one removes it and closes the gap.
  const toggle = (d: OrganizeDimension) =>
    onChange(dimensions.includes(d) ? dimensions.filter((x) => x !== d) : [...dimensions, d]);

  const counts = preview?.counts;

  return (
    <div className="min-w-0 space-y-3">
      <div className="space-y-1.5">
        <p className="text-sm font-medium text-foreground">{t.documents.organize.groupBy}</p>
        <div className="flex flex-wrap gap-2">
          {ORGANIZE_DIMENSIONS.map((d) => {
            const position = dimensions.indexOf(d);
            const active = position >= 0;
            return (
              <button
                key={d}
                type="button"
                aria-pressed={active}
                onClick={() => toggle(d)}
                className={cn(
                  "flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-sm transition-colors",
                  active
                    ? "border-primary bg-primary/10 font-medium text-primary"
                    : "border-border text-muted-foreground hover:bg-muted/50"
                )}
                title={
                  active
                    ? t.documents.organize.dimensionPosition.replace(
                        "{position}",
                        String(position + 1)
                      )
                    : undefined
                }
              >
                {active && (
                  <span className="flex h-4 w-4 items-center justify-center rounded-full bg-primary text-[10px] font-semibold tabular-nums text-primary-foreground">
                    {position + 1}
                  </span>
                )}
                {label(d)}
              </button>
            );
          })}
        </div>
        <p className="text-xs text-muted-foreground">{t.documents.organize.groupByHint}</p>
      </div>

      {dimensions.length === 0 ? (
        <p className="rounded-lg border border-dashed p-4 text-center text-sm text-muted-foreground">
          {t.documents.organize.noDimensions}
        </p>
      ) : (
        <div className="space-y-1.5">
          <div className="flex items-baseline justify-between gap-2">
            <p className="text-sm font-medium text-foreground">
              {t.documents.organize.previewLabel}
            </p>
            {counts && !isLoading && (
              <p className="text-xs tabular-nums text-muted-foreground">
                {t.documents.organize.previewStats
                  .replace("{moves}", String(counts.moves))
                  .replace("{folders}", String(counts.folders))}
              </p>
            )}
          </div>

          {/* overflow-x-auto, not hidden: a deep structure stays reachable by
              scrolling rather than being clipped or widening the modal. */}
          <div className="max-h-56 min-h-[5rem] overflow-auto rounded-lg border p-2">
            {isLoading ? (
              <p className="flex items-center gap-2 p-2 text-sm text-muted-foreground">
                <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
                {t.documents.organize.previewLoading}
              </p>
            ) : isError ? (
              <p className="flex items-center gap-1.5 p-2 text-sm text-destructive">
                <AlertCircle className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                {t.documents.organize.previewError}
              </p>
            ) : counts && counts.moves === 0 ? (
              <p className="p-2 text-sm text-muted-foreground">
                {t.documents.organize.previewEmpty}
              </p>
            ) : (
              <ProposalFolderTree
                proposal={preview?.proposal ?? null}
                documents={new Map()}
                showFiles={false}
              />
            )}
          </div>

          {capExceeded && counts && (
            <p className="flex items-start gap-1.5 text-xs text-destructive">
              <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
              {t.documents.organize.tagCapWarning
                .replace("{count}", String(counts.folders))
                .replace("{limit}", String(maxFolders))}
            </p>
          )}
          {!capExceeded && counts && counts.ambiguous > 0 && (
            <p className="text-xs text-muted-foreground">
              {t.documents.organize.ambiguousNote.replace("{count}", String(counts.ambiguous))}
            </p>
          )}
          {!capExceeded && counts && counts.kept > 0 && (
            <p className="text-xs text-muted-foreground">{t.documents.organize.untaggedNote}</p>
          )}
        </div>
      )}
    </div>
  );
}
